import { type ReactNode, useEffect, useState } from "react";
import {
  EmptyStatePage,
  GlassCard,
  Glyph,
  makeEntrance,
  usePrefersReducedMotion,
} from "@/components/application/empty-state-page";

const TEMPLATE_ICON =
  "M7 8.5h5M7 12h8m-5.316 6H16.2c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C21 15.72 21 14.88 21 13.2V7.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C18.72 3 17.88 3 16.2 3H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 5.28 3 6.12 3 7.8v12.535c0 .533 0 .8.11.937a.5.5 0 0 0 .39.188c.176 0 .384-.167.8-.5l2.385-1.908c.488-.39.731-.585 1.002-.724.241-.122.497-.212.762-.267C8.748 18 9.06 18 9.684 18Z";

const HOLD_MS = 3600;
const SWAP_MS = 420;

function Token({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-[5px] bg-brand-200/25 px-1.5 py-0.5 font-medium text-brand-200 ring-1 ring-brand-200/25 ring-inset">
      {children}
    </span>
  );
}

const MESSAGES: ReactNode[] = [
  <>
    Hey <Token>{"{creator_name}"}</Token>, we're aiming to get <Token>{"{product_name}"}</Token> live by{" "}
    <Token>{"{date}"}</Token> — how are you tracking?
  </>,
  <>
    Hey <Token>{"{creator_name}"}</Token>, payment for <Token>{"{campaign_name}"}</Token> is on its way — should land in{" "}
    <Token>{"{timeframe}"}</Token>.
  </>,
  <>
    Thanks for the draft, <Token>{"{creator_name}"}</Token>! A couple of small tweaks and we're good to go — sending
    notes now.
  </>,
];

/** Cycles the card body through the example messages, fading each out and the next in. */
function CyclingBody() {
  const reduceMotion = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (reduceMotion) return;
    let swapTimer: ReturnType<typeof setTimeout>;
    const holdTimer = setInterval(() => {
      setVisible(false);
      swapTimer = setTimeout(() => {
        setIndex((current) => (current + 1) % MESSAGES.length);
        setVisible(true);
      }, SWAP_MS);
    }, HOLD_MS + SWAP_MS);
    return () => {
      clearInterval(holdTimer);
      clearTimeout(swapTimer);
    };
  }, [reduceMotion]);

  const settled = reduceMotion || visible;
  return (
    <span
      className="block min-h-[48px] transition-all ease-out"
      style={{
        transitionDuration: `${SWAP_MS}ms`,
        opacity: settled ? 1 : 0,
        transform: settled ? "translateY(0)" : "translateY(6px)",
      }}
    >
      {MESSAGES[index]}
    </span>
  );
}

type MessageTemplatesEmptyStateProps = {
  onNewTemplate: () => void;
};

export function MessageTemplatesEmptyState({ onNewTemplate }: MessageTemplatesEmptyStateProps) {
  const reduceMotion = usePrefersReducedMotion();
  const entrance = makeEntrance(reduceMotion);

  return (
    <EmptyStatePage
      eyebrow="Message Templates"
      headline="Write it once, use it anywhere"
      subline="Save the messages that land and drop them into any conversation, invite or automation. One version, however many people are sending."
      measureCh={46}
      portraitSrc="/empty-states/portrait-message-templates.webp"
      portraitMaskLeft="22%"
      actionsDelaySeconds={0.65}
      primaryAction={{ label: "Create a template", onClick: onNewTemplate }}
      device={
        <div className="flex w-full max-w-[540px] flex-col gap-3">
          <GlassCard
            radius="xl"
            className="backdrop-blur-3xl"
            style={{ animation: entrance.glassIn(0.35) }}
            overlayStyle={{ animation: entrance.fadeIn(0.35) }}
          >
            <div className="flex items-start gap-4 px-5 py-4" style={{ animation: entrance.fadeInUp(0.35) }}>
              <span
                aria-hidden="true"
                className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-200 text-unbox-dark-brand-start"
                style={reduceMotion ? undefined : { animation: "trigger-pulse 2.6s ease-out 1.05s infinite" }}
              >
                <Glyph d={TEMPLATE_ICON} />
              </span>
              <div className="flex min-w-0 flex-col gap-1 pt-0.5">
                <p className="text-base font-semibold text-white">Welcome</p>
                <p className="text-sm leading-6 text-brand-100">
                  <CyclingBody />
                </p>
              </div>
            </div>
          </GlassCard>
        </div>
      }
    />
  );
}
